import { ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { inject } from 'mobx-react';
import { useHistory, useLocation } from 'react-router';
import * as React from 'react';

import { mergeQueryString, updateSearch } from 'bernie-core';

import {
  LodgingQueryParamsProvider,
  LodgingSortAndFilters,
  SortAndFiltersViewType,
  useLodgingURLParamsToQueryInputs,
} from '@shared-ui/search-tools-lodging';

import { evaluateAndLogExperiment } from 'src/common/stores/utils';
import { MultiItemStore } from 'src/common/stores/multi-item-store';
import { packageTypeMap } from 'src/common/stores/adapters';

import { buildUrlQueryString, Categories } from './universal-sort-filter-utils';

interface UniversalSortFilterProps {
  context?: ContextStore;
  experiment?: ExperimentStore;
  multiItem?: MultiItemStore;
  page?: PageStore;
}
interface UniversalSortFilterWrapperProps {
  className?: string;
  view: SortAndFiltersViewType;
}

const UniversalSortFilter: React.FC<UniversalSortFilterProps & UniversalSortFilterWrapperProps> = inject(
  'context',
  'experiment',
  'multiItem',
  'page'
)(({ context, experiment, multiItem, page, view, className = '' }) => {
  const [urlQueryString, setUrlQueryString] = React.useState('');

  const history = useHistory();
  const location = useLocation();
  const isFiltersDialog =
    [SortAndFiltersViewType.SORT_AND_FILTERS, SortAndFiltersViewType.FILTER_DIALOG, SortAndFiltersViewType.FILTER_BAR].includes(view) ??
    false;

  const updateSearchWithQuery = query => {
    const queryCombiner = (_prevQuery, newQuery) => newQuery;
    updateSearch({ history, location, newParams: query, replace: false, queryCombiner });
  };

  const onFieldChange = (field): void => {
    const { name, value } = field;
    const existingUrlQueryString = isFiltersDialog && urlQueryString !== '' ? urlQueryString : location.search;
    const newQueryString = buildUrlQueryString({ name, value, existingUrlQueryString });

    // this component renders several different types of filter views - when the child filter view is a dialog
    // we simply capture filter field changes as local state in the form of a new query string
    // if it's not a dialog, we go ahead and update the page's query string with the new one we generated
    if (isFiltersDialog) {
      setUrlQueryString(newQueryString);
    } else {
      updateSearchWithQuery(newQueryString);
      window.scrollTo(0, 0);
    }
  };

  const onSubmitFilters = React.useCallback(() => {
    const currentQueryString = urlQueryString === '' ? history.location.search : urlQueryString;
    const newQueryString = mergeQueryString(currentQueryString, null, key => !key.startsWith('pwaDialog'));

    setTimeout(() => {
      updateSearchWithQuery(newQueryString);
    }, 50);
    setUrlQueryString('');
  }, [urlQueryString]);

  const inputs = useLodgingURLParamsToQueryInputs();
  const { primary } = inputs?.propertySearchCriteriaInput ?? {};

  const multiItemObj = multiItem.multiItemContext?.id
    ? {
        id: multiItem.multiItemContext?.id,
        packageType: multiItem.multiItemContext?.packageType ? packageTypeMap[multiItem.multiItemContext.packageType] : null,
      }
    : null;

  const categories: Categories = {
    destination: primary?.destination ?? {},
    criteria: inputs?.propertySearchCriteriaInput ?? {},
    shoppingContext: {
      multiItem: multiItemObj,
    },
  };

  const onFieldClick = (field): void => {
    const { name } = field;

    if (name === 'sort') {
      evaluateAndLogExperiment('Update_Guest_Rating_sort_option_to_non_ML_Model', context, experiment, page);
    }
  };

  return (
    <div className={className}>
      <LodgingSortAndFilters
        onFieldChange={onFieldChange}
        onFieldClick={onFieldClick}
        onSubmit={onSubmitFilters}
        view={view}
        inputs={categories}
      />
    </div>
  );
});

export const UniversalSortFilterWrapper = ({ view, className }: UniversalSortFilterWrapperProps) => {
  const location = useLocation();

  return (
    <LodgingQueryParamsProvider urlSearchParams={new URLSearchParams(location.search)}>
      <UniversalSortFilter view={view} className={className} />
    </LodgingQueryParamsProvider>
  );
};
